import React, { useEffect, useMemo, useState } from 'react';

const GRADES = [
  { value: 1, label: '一年级' },
  { value: 2, label: '二年级' },
  { value: 3, label: '三年级' },
  { value: 4, label: '四年级' },
  { value: 5, label: '五年级' },
  { value: 6, label: '六年级' },
];

function AncientPoemPicker({ onSelect, onClose }) {
  const [selectedGrade, setSelectedGrade] = useState(1);
  const [selectedPoems, setSelectedPoems] = useState([]);
  const [ancientPoems, setAncientPoems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    import('../data/ancientPoems.json')
      .then((module) => {
        if (mounted) {
          setAncientPoems(module.default || []);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (mounted) {
          setAncientPoems([]);
          setIsLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const poems = useMemo(() => {
    const keyword = searchText.trim();
    return ancientPoems.filter((item) => {
      const gradeMatched = item.grade === selectedGrade;
      if (!keyword) return gradeMatched;
      return gradeMatched && (
        item.title?.includes(keyword) ||
        item.author?.includes(keyword)
      );
    });
  }, [ancientPoems, selectedGrade, searchText]);

  const getPoemKey = (poem) => poem.id || `${poem.grade}-${poem.title}-${poem.author}`;

  const handleGradeClick = (grade) => {
    setSelectedGrade(grade);
  };

  const handlePoemClick = (poem) => {
    const poemKey = getPoemKey(poem);
    setSelectedPoems(prev => {
      const exists = prev.some(item => getPoemKey(item) === poemKey);
      return exists
        ? prev.filter(item => getPoemKey(item) !== poemKey)
        : [...prev, poem];
    });
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1000,
        }}
        onClick={onClose}
      />

      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '560px',
          maxHeight: '80vh',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          <span style={{ fontSize: '15px', fontWeight: '600', color: '#333' }}>
            选择古诗词{selectedPoems.length > 0 ? `（已选${selectedPoems.length}首）` : ''}
          </span>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'none',
              fontSize: '18px',
              color: '#999',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            x
          </button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          <div
            style={{
              width: '90px',
              borderRight: '1px solid #f0f0f0',
              overflowY: 'auto',
              padding: '8px 0',
            }}
          >
            {GRADES.map((grade) => (
              <div
                key={grade.value}
                onClick={() => handleGradeClick(grade.value)}
                style={{
                  padding: '10px 12px',
                  fontSize: '13px',
                  color: selectedGrade === grade.value ? '#1890ff' : '#333',
                  background: selectedGrade === grade.value ? '#e6f7ff' : 'transparent',
                  cursor: 'pointer',
                  textAlign: 'center',
                }}
              >
                {grade.label}
              </div>
            ))}
          </div>

          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '12px',
            }}
          >
            <input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder="搜索诗题或作者"
              style={{
                width: '100%',
                padding: '8px 10px',
                marginBottom: '10px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontSize: '13px',
                outline: 'none',
              }}
            />
            {isLoading && (
              <div style={{ color: '#999', fontSize: '13px', textAlign: 'center', marginTop: '40px' }}>
                正在加载古诗词数据...
              </div>
            )}
            {!isLoading && poems.length === 0 && (
              <div style={{ color: '#999', fontSize: '13px', textAlign: 'center', marginTop: '40px' }}>
                没有找到匹配的古诗词
              </div>
            )}
            {poems.map((poem) => (
              <div
                key={poem.id || `${poem.title}-${poem.author}`}
                onClick={() => handlePoemClick(poem)}
                style={{
                  padding: '12px 14px',
                  marginBottom: '6px',
                  borderRadius: '8px',
                  border: selectedPoems.some(item => getPoemKey(item) === getPoemKey(poem)) ? '1px solid #1890ff' : '1px solid #f0f0f0',
                  background: selectedPoems.some(item => getPoemKey(item) === getPoemKey(poem)) ? '#f0f7ff' : 'white',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span
                    style={{
                      width: '14px',
                      height: '14px',
                      borderRadius: '3px',
                      border: selectedPoems.some(item => getPoemKey(item) === getPoemKey(poem)) ? '1px solid #1890ff' : '1px solid #d9d9d9',
                      background: selectedPoems.some(item => getPoemKey(item) === getPoemKey(poem)) ? '#1890ff' : 'white',
                      display: 'inline-block',
                    }}
                  />
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>
                    {poem.title}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            padding: '12px 20px',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              background: 'white',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            取消
          </button>
          <button
            onClick={() => selectedPoems.length > 0 && onSelect(selectedPoems, 'append')}
            disabled={selectedPoems.length === 0}
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              background: 'white',
              color: selectedPoems.length > 0 ? '#666' : '#bbb',
              cursor: selectedPoems.length > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            追加导入
          </button>
          <button
            onClick={() => selectedPoems.length > 0 && onSelect(selectedPoems, 'replace')}
            disabled={selectedPoems.length === 0}
            style={{
              padding: '8px 16px',
              fontSize: '12px',
              border: 'none',
              borderRadius: '4px',
              background: selectedPoems.length > 0 ? '#1890ff' : '#d9d9d9',
              color: 'white',
              cursor: selectedPoems.length > 0 ? 'pointer' : 'not-allowed',
            }}
          >
            替换导入
          </button>
        </div>
      </div>
    </>
  );
}

export default AncientPoemPicker;
